export interface MachineRepairUserRef {
  _id: string;
  firstName?: string;
  lastName?: string;
  userName?: string;
  email?: string;
}

export type MachineRepairPriority = 'high' | 'medium' | 'low';
export type MachineRepairStatus = 'open' | 'answered' | 'closed';
export type MachineRepairMessageKind = 'request' | 'response';

export interface MachineRepairRecipient {
  userId: MachineRepairUserRef | string;
  username?: string;
}

export interface MachineRepairMessage {
  _id: string;
  author?: MachineRepairUserRef | string | null;
  authorName?: string;
  kind: MachineRepairMessageKind;
  text: string;
  createdAt: string;
}

export interface MachineRepairThread {
  _id: string;
  number?: string;
  rnWorkOrder: string;
  subprocessId: string;
  machineId: string;
  subject: string;
  priority: MachineRepairPriority;
  status: MachineRepairStatus;
  recipients: MachineRepairRecipient[];
  messages: MachineRepairMessage[];
  createdBy?: MachineRepairUserRef | string | null;
  createdAt: string;
  updatedAt: string;
  dateTo?: string | null;
}

export interface CreateMachineRepairThreadPayload {
  rnWorkOrder: string;
  subprocessId: string;
  machineId: string;
  subject: string;
  priority: MachineRepairPriority;
  recipients: string[];
  text: string;
  dateTo?: string | null;
}

// POST /:id/messages body. Omitting `dateTo` = plain reply (deadline unchanged / "accept");
// sending it saves the agreed deadline on the thread, Team Plan rows and calendar entry.
export interface AddMachineRepairMessagePayload {
  text: string;
  dateTo?: string | null;
}

// GET returns a paginated envelope: `{ success, data, pagination: { page, limit, total, pages } }`.
// Read it defensively — tolerate a bare array and top-level pagination fields too —
// so a small shape difference doesn't break the screen.
export interface MachineRepairThreadListResponse {
  data: MachineRepairThread[];
  page: number;
  totalPages: number;
  total: number;
  nextNumber?: string;
}

export const normalizeThreadListResponse = (payload: unknown): MachineRepairThreadListResponse => {
  if (Array.isArray(payload)) {
    return { data: payload as MachineRepairThread[], page: 1, totalPages: 1, total: payload.length };
  }
  const envelope = (payload || {}) as Partial<MachineRepairThreadListResponse> & {
    rows?: MachineRepairThread[];
    pagination?: { page?: number; total?: number; pages?: number };
  };
  const data = Array.isArray(envelope.data) ? envelope.data : Array.isArray(envelope.rows) ? envelope.rows : [];
  const pagination = envelope.pagination;
  return {
    data,
    page: Number(pagination?.page ?? envelope.page) || 1,
    totalPages: Number(pagination?.pages ?? envelope.totalPages) || 1,
    total: Number(pagination?.total ?? envelope.total) || data.length,
    nextNumber: typeof envelope.nextNumber === 'string' ? envelope.nextNumber : '',
  };
};

// Mutating endpoints answer `{ message, data: Thread }`; tolerate a bare Thread too.
export const normalizeThreadResponse = (payload: unknown): MachineRepairThread | null => {
  if (!payload || typeof payload !== 'object') return null;
  const envelope = payload as { data?: unknown; _id?: unknown };
  if (envelope.data && typeof envelope.data === 'object' && typeof (envelope.data as { _id?: unknown })._id === 'string') {
    return envelope.data as MachineRepairThread;
  }
  if (typeof envelope._id === 'string') {
    return payload as MachineRepairThread;
  }
  return null;
};

export const machineRepairUserLabel = (user?: MachineRepairUserRef | string | null): string => {
  if (!user || typeof user === 'string') return '';
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return fullName || user.userName || user.email || '';
};
